import type { SupplierMeasurementCapability } from '@tandemone/types'

import { Badge } from '@/components/ui/badge'
import { SUPPLIER_MEASUREMENT_EQUIPMENT_OPTIONS } from '@/domains/supplier-onboarding'

import {
  type MeasurementFamily,
  getMeasurementFamily,
  MEASUREMENT_FAMILY_DOT_CLASSES,
  MEASUREMENT_FAMILY_LABELS,
  MEASUREMENT_FAMILY_ORDER
} from './measurement-family'
import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

export function ProfileMeasurement({
  companyName,
  rows
}: {
  companyName: string
  rows: SupplierMeasurementCapability[]
}) {
  const byFamily = new Map<MeasurementFamily, SupplierMeasurementCapability[]>()
  for (const row of rows) {
    const { family } = getMeasurementFamily(row.equipmentType)
    const list = byFamily.get(family) ?? []
    list.push(row)
    byFamily.set(family, list)
  }
  return (
    <ProfileSection title={`${companyName} Measurement Capabilities`}>
      {rows.length === 0 ? (
        <ProfileEmpty message='No measurement operations listed.' />
      ) : (
        <div className='flex flex-col gap-5'>
          {MEASUREMENT_FAMILY_ORDER.filter(family => byFamily.has(family)).map(
            family => (
              <div key={family} className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <span
                    className={`inline-block size-2 rounded-full ${MEASUREMENT_FAMILY_DOT_CLASSES[family]}`}
                  />
                  <h3 className='text-xs uppercase tracking-wide text-muted-foreground'>
                    {MEASUREMENT_FAMILY_LABELS[family]}
                  </h3>
                </div>
                <div className='flex flex-col divide-y divide-border'>
                  {byFamily.get(family)!.map(row => {
                    const option =
                      SUPPLIER_MEASUREMENT_EQUIPMENT_OPTIONS[row.equipmentType]
                    return (
                      <div
                        key={row.id}
                        className='flex flex-col gap-1.5 py-1.5 sm:grid sm:grid-cols-[1fr_auto] sm:items-center sm:gap-0'
                      >
                        <span className='text-sm text-foreground'>
                          {option.label}
                        </span>
                        <span className='flex flex-wrap items-center gap-2 sm:justify-end sm:pl-3'>
                          <Badge
                            variant='outline'
                            className={`w-28 justify-center ${
                              row.inHouse
                                ? 'border-emerald-300 text-emerald-800'
                                : 'border-border text-muted-foreground/40'
                            }`}
                          >
                            in-house
                          </Badge>
                          <Badge
                            variant='outline'
                            className={`w-28 justify-center ${
                              row.subContracted
                                ? 'border-amber-300 text-amber-800'
                                : 'border-border text-muted-foreground/40'
                            }`}
                          >
                            subcontracted
                          </Badge>
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </ProfileSection>
  )
}
